import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, first, map, switchMap, takeUntil } from 'rxjs/operators';

import { Tab } from '@vi-iot/ui';

import { AuthStore, SearchListType } from '@store';

import { GTMService } from '../../../shared/services';
import { SearchService } from '../../search.service';

@Component({
  selector: 'app-search-fields',
  templateUrl: './search-fields.component.html',
  styleUrls: ['./search-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchFieldsComponent implements OnInit, OnDestroy {
  activeSearch$: Observable<string>;
  tabs: Tab[] = [];

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private searchService: SearchService,
    private translate: TranslateService,
    private gtmService: GTMService,
    private authStore: AuthStore
  ) {
    const tabs = [
      {
        name: '+SEARCH.SEARCH_BY_GATEWAY.TITLE',
        active: true,
        value: 'gateway',
      },
      {
        name: '+SEARCH.SEARCH_BY_USER.TITLE',
        active: false,
        value: 'user',
      },
      {
        name: '+SEARCH.SEARCH_BY_ERROR.TITLE',
        active: false,
        value: 'error',
      },
    ];

    this.authStore
      .isRsaUserWithWriteRights()
      .pipe(
        first(),
        switchMap((isRsaWithWriteRights) => {
          if (isRsaWithWriteRights) {
            tabs.push({
              name: '+SEARCH.ACTIVATION_CODES.TITLE',
              active: false,
              value: 'activationCodes',
            });
          }

          return combineLatest(
            tabs.map((tab) =>
              this.translate.stream(tab.name).pipe(map((name) => ({ ...tab, name })))
            )
          );
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((translatedTabs) => {
        this.tabs = translatedTabs;
      });
  }

  ngOnInit(): void {
    this.activeSearch$ = this.searchService.activeSearch$;
    this.searchService.onSearchHistoryEntryClicked$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((data) => data.searchParams)
      )
      .subscribe((searchParams) => {
        if (searchParams.type === SearchListType.Error) {
          this.searchService.errorSearchParams$.next(searchParams);
        } else if (
          searchParams.type === SearchListType.Gateway ||
          searchParams.type === SearchListType.Boiler
        ) {
          this.searchService.gatewaySearchParams$.next(searchParams);
        } else if (searchParams.type === SearchListType.User) {
          this.searchService.userSearchParams$.next(searchParams);
        }
      });
  }

  onSearchOptionChange(tab: Tab): void {
    this.searchService.activeSearch$.next(tab.value);
    this.gtmService.track('onSelect', `${tab.value} tab`, 'Search page');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
